/** @module csi/slicer/slicerConfig */
define([
	"dojo/_base/declare",
	"dojo/topic",
	"csi/_base/underscore",
	"csi/constants",
	"csi/html"
], function(declare, topic, _, constants, html) {

	/** Classifier id attribute */
	const constName = "SLICER_CLASSIFIER_ID";
	html.registerDataAttribute(constName, constName);

	return declare(null, {
		/**
		 * Actual holder for Slicer configuration 
		 * @property {Object} _config
		 *  This variable must not be accessed directly for read/write
		 */
		_config: null,

		/** @property {Object} widget */
		widget: null,

		/** @property {boolean} bQuickUpdate - flag to prevent viewRebuild on current config set action */
		bQuickUpdate: false,

		/** @property {string} _SLICER_CONFIG_TOPIC - subscription topic */
		SLICER_CONFIG_TOPIC: "/csi/slicer/configChanged",

		/** 
		 * Set new value of config. Isolate local saved config from future changes on val object.
		 * @param {Object} newConfig - new configuration object
		 * @param {boolean} bQuickUpdate - quick update, mean "save new values but do not rebuild the view"
		 * @param {boolean} bNoShowWait - if defined and set to true will disable showWait call"
		 */
		set: function(newConfig, bQuickUpdate, bNoShowWait) {
			console.assert(newConfig.rowClassifiers && newConfig.columnClassifiers);
			if (this.isEqual(newConfig)) {
				return;
			}
			if (!bNoShowWait) {
				html.showWait(true);
			}

			var bInit = !this._config;
			this.bQuickUpdate = bQuickUpdate || false;

			if (!bInit) {
				// Changing metrics position or columnClassifiers set or order will render column sizes meaningless 
				if (!_.isEqual(newConfig.columnClassifiers, this._config.columnClassifiers) ||
					!_.isEqual(newConfig.metrics, this._config.metrics) ||
					newConfig.metricsPos != this._config.metricsPos) {
					newConfig.columnSizes = {};
				}
				if (newConfig.compareTo.val && (
					(newConfig.columnClassifiers.length == 0) ||
					(newConfig.columnClassifiers[0] != newConfig.compareTo.key) ||
					(newConfig.metricsPos == 0))) {
					newConfig.compareTo = {};
				}
			}
			this._config = _.clone(newConfig);
			if (!bInit) {
				var self = this;
				_.delay(function() {
					topic.publish(self.SLICER_CONFIG_TOPIC, self.widget);
				}, 150); // transition duration is 0.1s, we need to wait longer to allow wait cursor to appear
			}
		},

		/** 
		 * Get config. Isolate local value from future changes in returned object
		 * @return {Object} config
		 */
		get: function() {
			// This assert tests if local value was initiated before first call to "get" function
			console.assert(this._config.rowClassifiers && this._config.columnClassifiers);
			var newObj = {};
			var oldObj = this._config;
			newObj.rowClassifiers = oldObj.rowClassifiers.slice(0);
			newObj.columnClassifiers = oldObj.columnClassifiers.slice(0);
			newObj.columnSizes = _.clone(oldObj.columnSizes);
			newObj.compareTo = _.clone(oldObj.compareTo);
			newObj.classifierStore = oldObj.classifierStore;
			newObj.flatSlicer = oldObj.flatSlicer;
			newObj.metrics = oldObj.metrics.slice(0);
			newObj.metricStore = oldObj.metricStore;
			newObj.metricsPos = oldObj.metricsPos;
			newObj.metricsHl = _.clone(oldObj.metricsHl);
			newObj.metricsHlVisible = _.clone(oldObj.metricsHlVisible);
			newObj.expandAll = oldObj.expandAll;
			newObj.showTotals = _.clone(oldObj.showTotals);
			newObj.stickyRows = oldObj.stickyRows;
			return newObj;
		},

		/**
		 * Import classifiers and metrics 
		 * @param {string[]} rowClassifiers
		 * @param {string[]} columnClassifiers
		 * @param {string[]} metrics 
		 */
		importCfg: function(rowClassifiers, columnClassifiers, metrics, classifierStore, metricStore,
			metricsPos, metricsHl, metricsHlVisible, expandAll, stickyRows, columnSizes,
			compareTo, showTotals, flatSlicer) {
			var config = {};
			config.rowClassifiers = rowClassifiers.slice(0);
			config.columnClassifiers = columnClassifiers.slice(0);
			config.columnSizes = _.clone(columnSizes);
			config.compareTo = _.clone(compareTo);
			config.classifierStore = classifierStore;
			config.flatSlicer = flatSlicer;
			config.metrics = metrics.slice(0);
			config.metricStore = metricStore;
			config.metricsPos = metricsPos;
			config.metricsHl = _.clone(metricsHl);
			config.metricsHlVisible = _.clone(metricsHlVisible);
			config.expandAll = expandAll;
			config.showTotals = _.clone(showTotals);
			config.stickyRows = stickyRows;
			this.set(config);
		},

		/**
		 * Return false if newConfig has any difference
		 * @param {Object} newConfig - configuration to be compared with own configuration
		 */
		isEqual: function(newConfig) {
			return _.isEqual(newConfig, this._config);
		},

		/** 
		 * Subscribe to configuration change notifications
		 * @param {Function} func
		 * @return {Object} handle
		 */
		subscribe: function(func) {
			console.assert(!!func);
			return topic.subscribe(this.SLICER_CONFIG_TOPIC, func);
		},

		/**
		 * Unsubscribe from configuration change notifications
		 * @param {Object} handle
		 */
		unsubscribe: function(handle) {
			console.assert(!!handle);
			if (handle && handle.remove) {
				handle.remove();
				handle = null;
			}
		},

		/** 
		 * Parse Columns, Rows and Metrics containers containers and recreate configuration
		 * @param {Object} cntRows - container for row classifiers
		 * @param {Object} cntCols - container for column  classifiers
		 * @param {Object} cntMetrics - container for metrics
		 */
		recreatePivotConfig: function(cntRows, cntCols, cntMetrics, textNodeClass) {
			function getClsId(node) {
				return html.getDataAttribute(node, html.SLICER_CLASSIFIER_ID);
			}

			var config = this.get();

			config.rowClassifiers.length = 0;
			config.columnClassifiers.length = 0;

			_.each($(cntRows.domNode).find(textNodeClass), function(node) {
				config.rowClassifiers.push(getClsId(node));
			});
			_.each($(cntCols.domNode).find(textNodeClass), function(node, idx) {
				var clsId = getClsId(node);
				if (clsId) {
					config.columnClassifiers.push(clsId);
				} else {
					config.metricsPos = idx;
				}
			});
			if (config.metricsPos >= config.columnClassifiers.length) {
				config.metricsPos = constants.SLICER_MAX_METRICS_POS;
			}

			if (cntMetrics) {
				config.metrics.length = 0;
				_.each($(cntMetrics.domNode).find(textNodeClass), function(node) {
					config.metrics.push(getClsId(node));
				});
			}

			this.set(config);
		}
	});
});
